import { useCallback } from "react";
import { useCustomNavigate } from "./useCustomNavigate";
import { apiRequest } from "../utils/api";

interface ChatLimitsResponse {
  success: boolean;
  is_free: boolean;
  max_chats: number;
  current_count: number;
  latest_chat_uuid: string | null;
}

/**
 * チャットナビゲーション用のカスタムフック
 * チャットのリミットや現在のヒストリー数を確認して、適切なナビゲーション先を決定します
 */
export const useChatNavigate = () => {
  const navigate = useCustomNavigate();

  /**
   * チャット制限情報を取得する
   */
  const getChatLimits = useCallback(async (): Promise<ChatLimitsResponse | null> => {
    try {
      const res = await apiRequest("/api/v1/chat/limits", "GET");
      return res.data as ChatLimitsResponse;
    } catch (error) {
      console.error("Failed to fetch chat limits:", error);
      return null;
    }
  }, []);

  /**
   * チャット画面に移動する
   * - chatUuidが指定されていれば、そのチャットに直接移動
   * - 指定されていない場合は、チャット制限をチェックして適切なチャットに移動
   *   - 制限に達していて最新のチャットがある場合は、最新のチャットに移動
   *   - 制限に達していなければ、新規チャット（/chat/）に移動
   */
  const navigateToChat = useCallback(
    async (chatUuid?: string) => {
      // チャットUUIDが指定されている場合は直接そのチャットに移動
      if (chatUuid) {
        navigate(`/chat/${chatUuid}`);
        return;
      }

      try {
        // チャット制限情報を取得
        const limits = await getChatLimits();

        if (!limits) {
          // 制限情報が取得できなかった場合はデフォルトの挙動
          navigate("/chat");
          return;
        }

        // リミットに達していて、最新のチャットがある場合はそのチャットにリダイレクト
        if (limits.current_count >= limits.max_chats && limits.latest_chat_uuid) {
          navigate(`/chat/${limits.latest_chat_uuid}`);
        } else {
          // リミットに達していない場合は新規チャット画面へ
          navigate("/chat");
        }
      } catch (error) {
        console.error("チャットナビゲーションエラー:", error);
        // エラーが発生した場合はデフォルトの挙動
        navigate("/chat");
      }
    },
    [navigate, getChatLimits]
  );

  /**
   * 新しいチャットを作成して移動する
   * - force_delete=trueパラメータでAPIを呼び出し、制限を超えている場合は古いチャットを自動的に削除
   */
  const createAndNavigateToNewChat = useCallback(async () => {
    try {
      const res = await apiRequest("/api/v1/chat/", "POST", { force_delete: true });
      if (res && res.data && res.data.path) {
        navigate(res.data.path);
        return true;
      }
      return false;
    } catch (error) {
      console.error("新規チャット作成エラー:", error);
      return false;
    }
  }, [navigate]);

  return {
    navigateToChat,
    createAndNavigateToNewChat,
    getChatLimits,
  };
};
